import { fork, put, takeLatest, select } from "redux-saga/effects";

import {
  fetchSavedInvoiceData,
  updateSavedInvoiceData,
  fetchInvoiceCountries
} from "./index";
import ApiClient from "../../../api/ApiClient";
import { getUserData } from "../auth/login/selector";
import { actions as alertActions } from "../alerts";
import { getLocale, getTranslations } from "../translations/selector";
import {
  updateSavedInvoiceDataErrorsToFormErrors,
  fieldNameToFieldLabeld
} from "./dto/updateSavedInvoiceDataErrorsToFormErrors";
import {
  apiInvoiceDataToSavedSlices,
  formSliceToApiInvoiceData
} from "./dto/invoiceDataMappers";
import { FORM_DATA_KEY_BY_KIND, SAVED_DATA_KEY_BY_KIND } from "./invoiceFields";
import { getBusinessActivityKind } from "./selector";

export function* onFetchSavedInvoiceData() {
  const userData = yield select(getUserData);
  const emptySlices = apiInvoiceDataToSavedSlices(null);

  if (!userData) {
    yield put(fetchSavedInvoiceData.success(emptySlices));
    return;
  }

  try {
    const response = yield ApiClient.get({
      urlPath: "app.invoices.invoiceData"
    });

    yield put(
      fetchSavedInvoiceData.success(apiInvoiceDataToSavedSlices(response?.data))
    );
  } catch (error) {
    if (error?.response?.status === 404) {
      yield put(fetchSavedInvoiceData.success(emptySlices));
      return;
    }
    yield put(fetchSavedInvoiceData.failure());
    console.error(error);
  }
}

export function* onUpdateSavedInvoiceData({ payload }) {
  const { skipSuccessAlert = false, invoiceFormData } = payload;
  const invoicesEnabled = invoiceFormData?.invoicesEnabled;

  let businessActivityKind;
  let formDataKey;
  let formSlice;

  if (invoicesEnabled === false) {
    // Disabling must not modify the invoice data already saved on the BE:
    // re-send the last saved kind/fields unchanged, only flipping the flag.
    businessActivityKind = yield select(getBusinessActivityKind);

    if (!businessActivityKind) {
      // Prevent sending empty invoice data to the BE if user has never saved any data before.
      yield put(updateSavedInvoiceData.success());
      return;
    }

    formDataKey = FORM_DATA_KEY_BY_KIND[businessActivityKind];
    formSlice = yield select(
      state => state.invoices[SAVED_DATA_KEY_BY_KIND[businessActivityKind]]
    );
  } else {
    businessActivityKind = invoiceFormData?.businessActivityKind;

    if (!businessActivityKind) {
      // Nothing to save (e.g. transient payment-bar checkbox left unchecked).
      yield put(updateSavedInvoiceData.success());
      return;
    }

    formDataKey = FORM_DATA_KEY_BY_KIND[businessActivityKind];
    formSlice = invoiceFormData?.[formDataKey];
  }

  try {
    const data = formSliceToApiInvoiceData({
      businessActivityKind,
      formSlice,
      invoicesEnabled
    });

    const response = yield ApiClient.post({
      urlPath: "app.invoices.invoiceData",
      data
    });

    if (!skipSuccessAlert) {
      yield put(
        alertActions.addSuccessAlert({
          title: "invoices-alert_success"
        })
      );
    }
    yield put(
      updateSavedInvoiceData.success(
        apiInvoiceDataToSavedSlices(response?.data)
      )
    );
  } catch (e) {
    const data = e?.response?.data;
    console.error(e);

    if (data?.type === "validation_error" && Array.isArray(data?.errors)) {
      const translations = yield select(getTranslations);
      const locale = yield select(getLocale);

      const formErrors = updateSavedInvoiceDataErrorsToFormErrors(
        data.errors,
        formDataKey
      );
      const activeFormErrors = formErrors[formDataKey] ?? {};

      for (const [key, value] of Object.entries(activeFormErrors)) {
        const fieldLabel = fieldNameToFieldLabeld[key];
        const translatedFieldLabel =
          translations?.[locale]?.[fieldLabel] ?? fieldLabel;
        yield put(
          alertActions.addErrorAlert({
            title: `${translatedFieldLabel}: ${value}`
          })
        );
      }

      yield put(
        updateSavedInvoiceData.failure({
          [formDataKey]: Object.keys(activeFormErrors).length
            ? activeFormErrors
            : null
        })
      );

      return;
    }

    yield put(
      alertActions.addErrorAlert({
        title: "invoices-alert_error"
      })
    );
    yield put(updateSavedInvoiceData.failure());
  }
}

export function* onFetchInvoiceCountries() {
  try {
    const response = yield ApiClient.get({
      urlPath: "app.invoices.invoiceCountries"
    });

    yield put(
      fetchInvoiceCountries.success({
        countries: response?.data?.countries ?? []
      })
    );
  } catch (error) {
    console.error(error);
    yield put(fetchInvoiceCountries.failure());
  }
}

function* watchInvoices() {
  yield takeLatest(fetchSavedInvoiceData.TRIGGER, onFetchSavedInvoiceData);
  yield takeLatest(updateSavedInvoiceData.TRIGGER, onUpdateSavedInvoiceData);
  yield takeLatest(fetchInvoiceCountries.TRIGGER, onFetchInvoiceCountries);
}

export default [fork(watchInvoices)];
